import { FormEvent, useCallback, useEffect, useState } from "react";

import { AgentWorkspace } from "./App";
import type { AuthSession, AuthUser } from "./authTypes";


async function readJson<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as T & { detail?: string };
  if (!response.ok) {
    throw new Error(body.detail || `Ошибка ${response.status}`);
  }
  return body;
}


function LoginScreen({ onLogin }: { onLogin: (session: AuthSession) => void }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const response = await fetch("api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login, password }),
      });
      onLogin(await readJson<AuthSession>(response));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось войти");
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand login-brand">
          <span className="brand-mark">ЛМ</span>
          <div>
            <p className="eyebrow">ЛетоМаркет · защищённый стенд</p>
            <h1>ИИ-продажник</h1>
          </div>
        </div>
        <div className="login-copy">
          <p className="eyebrow">Вход для команды</p>
          <h2>Войдите в стенд</h2>
          <p>Каждому сотруднику нужна своя учётная запись.</p>
        </div>
        <form className="login-form" onSubmit={submit}>
          <label>
            Логин или почта
            <input
              autoComplete="username"
              autoFocus
              onChange={(event) => setLogin(event.target.value)}
              required
              value={login}
            />
          </label>
          <label>
            Пароль
            <input
              autoComplete="current-password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="primary-button" disabled={pending} type="submit">
            {pending ? "Проверяю…" : "Войти"}
          </button>
        </form>
      </section>
    </main>
  );
}


function ChangePasswordScreen({
  session,
  onChanged,
}: {
  session: AuthSession;
  onChanged: (session: AuthSession) => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmation) {
      setError("Новые пароли не совпадают");
      return;
    }
    setPending(true);
    setError("");
    try {
      const response = await fetch("api/auth/change-password", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": session.csrfToken,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      onChanged(await readJson<AuthSession>(response));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось изменить пароль");
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="login-copy password-copy">
          <p className="eyebrow">Защита учётной записи</p>
          <h2>Задайте новый пароль</h2>
          <p>Начальный пароль больше не будет приниматься. Нужно не меньше 12 символов, заглавная, строчная буква и цифра.</p>
        </div>
        <form className="login-form" onSubmit={submit}>
          <label>
            Текущий пароль
            <input autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value)} required type="password" value={currentPassword} />
          </label>
          <label>
            Новый пароль
            <input autoComplete="new-password" minLength={12} onChange={(event) => setNewPassword(event.target.value)} required type="password" value={newPassword} />
          </label>
          <label>
            Повторите новый пароль
            <input autoComplete="new-password" minLength={12} onChange={(event) => setConfirmation(event.target.value)} required type="password" value={confirmation} />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="primary-button" disabled={pending} type="submit">
            {pending ? "Сохраняю…" : "Сменить пароль"}
          </button>
        </form>
      </section>
    </main>
  );
}


type AdminPanelProps = {
  session: AuthSession;
  onClose: () => void;
  onExpired: () => void;
};


function AdminPanel({ session, onClose, onExpired }: AdminPanelProps) {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [issued, setIssued] = useState<{ login: string; password: string } | null>(null);
  const [success, setSuccess] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const headers = { "X-CSRF-Token": session.csrfToken };

  const request = useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      const response = await fetch(path, {
        ...init,
        credentials: "same-origin",
        headers: { ...headers, ...(init.headers || {}) },
      });
      if (response.status === 401) onExpired();
      return readJson<T>(response);
    },
    [session.csrfToken, onExpired],
  );

  const loadUsers = useCallback(async () => {
    try {
      const result = await request<{ users: AuthUser[] }>("api/admin/users");
      setUsers(result.users);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось загрузить доступы");
    }
  }, [request]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const createUser = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setCreating(true);
    try {
      const result = await request<{ user: AuthUser }>(
        "api/admin/users",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            login: email,
            display_name: displayName,
            password,
          }),
        },
      );
      setSuccess(`Доступ для ${result.user.displayName} создан`);
      setIssued(null);
      setEmail("");
      setDisplayName("");
      setPassword("");
      await loadUsers();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось создать доступ");
    } finally {
      setCreating(false);
    }
  };

  const resetPassword = async (user: AuthUser) => {
    if (!window.confirm(`Сбросить пароль для ${user.displayName}?`)) return;
    setError("");
    try {
      const result = await request<{ password: string }>(
        `api/admin/users/${user.id}/reset-password`,
        { method: "POST" },
      );
      setIssued({ login: user.login, password: result.password });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось сбросить пароль");
    }
  };

  const toggleUser = async (user: AuthUser) => {
    setError("");
    try {
      await request(`api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !user.isActive }),
      });
      await loadUsers();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось изменить доступ");
    }
  };

  return (
    <div className="admin-overlay" role="dialog" aria-modal="true" aria-label="Управление доступами">
      <section className="admin-panel">
        <div className="admin-heading">
          <div>
            <p className="eyebrow">Команда</p>
            <h2>Управление пользователями</h2>
            <p className="admin-intro">Все пользователи получают полный доступ ко всем разделам.</p>
          </div>
          <button className="ghost-button" onClick={onClose} type="button">Закрыть</button>
        </div>

        <form className="admin-create" onSubmit={createUser}>
          <label>
            Имя сотрудника
            <input autoComplete="off" onChange={(event) => setDisplayName(event.target.value)} required value={displayName} />
          </label>
          <label>
            Электронная почта
            <input autoComplete="off" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />
          </label>
          <label>
            Пароль
            <input autoComplete="new-password" minLength={8} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} />
          </label>
          <button className="primary-button" disabled={creating} type="submit">
            {creating ? "Создаю…" : "Добавить пользователя"}
          </button>
        </form>

        {success ? <p className="form-success">{success}</p> : null}

        {issued ? (
          <div className="issued-password">
            <p><strong>Передайте сотруднику. Пароль показывается один раз.</strong></p>
            <code>{issued.login}</code>
            <code>{issued.password}</code>
            <button
              className="header-button"
              onClick={() => void navigator.clipboard.writeText(`${issued.login}\n${issued.password}`)}
              type="button"
            >Скопировать</button>
          </div>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}
        <div className="users-list">
          {users.map((user) => (
            <article className={`user-row ${user.isActive ? "" : "inactive"}`} key={user.id}>
              <div>
                <strong>{user.displayName}</strong>
                <span>{user.login}</span>
              </div>
              <span className="user-role">{user.isActive ? "Полный доступ" : "Отключён"}</span>
              {user.id !== session.user.id ? (
                <div className="user-actions">
                  <button className="header-button" onClick={() => void resetPassword(user)} type="button">Новый пароль</button>
                  <button className="header-button" onClick={() => void toggleUser(user)} type="button">
                    {user.isActive ? "Отключить" : "Включить"}
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}


export default function AuthApp() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);

  const expire = useCallback(() => {
    setSession(null);
    setShowAdmin(false);
  }, []);

  useEffect(() => {
    fetch("api/auth/me", { credentials: "same-origin" })
      .then((response) => (response.ok ? readJson<AuthSession>(response) : null))
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setLoading(false));
  }, []);

  const logout = async () => {
    if (!session) return;
    await fetch("api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": session.csrfToken },
    }).catch(() => undefined);
    expire();
  };

  if (loading) {
    return <main className="login-shell"><p className="loading-access">Проверяю доступ…</p></main>;
  }
  if (!session) return <LoginScreen onLogin={setSession} />;
  if (session.user.mustChangePassword) {
    return <ChangePasswordScreen session={session} onChanged={setSession} />;
  }

  return (
    <>
      <AgentWorkspace
        csrfToken={session.csrfToken}
        user={session.user}
        onLogout={() => void logout()}
        onOpenAdmin={() => setShowAdmin(true)}
        onSessionExpired={expire}
      />
      {showAdmin ? <AdminPanel session={session} onClose={() => setShowAdmin(false)} onExpired={expire} /> : null}
    </>
  );
}
